import React, { useEffect, useState, useRef } from "react";
import { db } from "../firebase";
import { collection, query, where, orderBy, onSnapshot, addDoc, serverTimestamp } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { FaPaperPlane, FaSpinner } from "react-icons/fa";
import { notifyUser, notifyRoles } from "../utils/notifications";
import "./ChatRoom.css";

// --- THIS IS THE UPDATED FUNCTION ---
const formatTimestamp = (timestamp) => {
  if (!timestamp?.toDate) return "...";
  // Switched from toLocaleTimeString to toLocaleString with options
  return timestamp.toDate().toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }) + ' (IST)';
};

const ChatRoom = ({ selectedEmployee }) => {
  const { currentUser, userData } = useAuth();
  const isManagementView = !!selectedEmployee;

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const scrollRef = useRef();

  // --- ROLE-BASED LOGIC ---
  const targetUid = isManagementView ? selectedEmployee.uid : currentUser.uid;

  let chatPartnerName = "Support";
  if (isManagementView) {
    chatPartnerName = selectedEmployee.name;
  } else if (userData.role === 'employee') {
    chatPartnerName = "Admin / Superadmin";
  } else if (userData.role === 'admin') {
    chatPartnerName = "Superadmin";
  }

  useEffect(() => {
    if (!targetUid) return;
    setLoading(true);

    const q = query(
      collection(db, "chat"),
      where("targetUid", "==", targetUid), // Query using the correct 'targetUid' field
      orderBy("timestamp")
    );

    const unsubscribe = onSnapshot(q, (snap) => {
      const msgs = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setMessages(msgs);
      setLoading(false);
      setTimeout(() => {
        scrollRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }, (error) => {
      console.error("Chat onSnapshot Error:", error);
      // Removed alert() as it's not mobile-friendly
      console.error("Error fetching chat. Check Firestore rules and indexes.");
      setLoading(false);
    });

    return () => unsubscribe();
  }, [targetUid]);

  const sendMessage = async () => {
    if (!newMessage.trim() || !currentUser) return;
    setIsSending(true);

    const messageData = {
      targetUid, // The UID that defines the thread
      senderUid: currentUser.uid,
      senderName: userData.name,
      text: newMessage,
      timestamp: serverTimestamp(),
    };

    try {
      setNewMessage(""); // Optimistically clear input
      await addDoc(collection(db, "chat"), messageData);

      // ── Notify recipient(s) per hierarchy ────────────────────────────────
      const snippet = newMessage.length > 60 ? newMessage.slice(0, 60) + '…' : newMessage;
      if (isManagementView) {
        // Admin/superadmin messaging an employee → notify that employee
        notifyUser({
          recipientUid: selectedEmployee.uid,
          type: 'chat_message',
          title: `New message from ${userData.name || 'Admin'}`,
          body: snippet,
          link: '/dashboard',
          senderUid: currentUser.uid,
          senderName: userData.name || '',
          metadata: { targetUid },
        });
      } else if (userData.role === 'employee') {
        // Employee messaging → notify admins + superadmins
        notifyRoles({
          roles: ['admin', 'superadmin'],
          type: 'chat_message',
          title: `New message from ${userData.name || 'Employee'}`,
          body: snippet,
          link: '/dashboard/manage',
          excludeUid: currentUser.uid,
          senderUid: currentUser.uid,
          senderName: userData.name || '',
          metadata: { targetUid },
        });
      } else if (userData.role === 'admin') {
        // Admin in own chat thread → notify superadmins
        notifyRoles({
          roles: ['superadmin'],
          type: 'chat_message',
          title: `New message from ${userData.name || 'Admin'}`,
          body: snippet,
          link: '/dashboard/manage',
          excludeUid: currentUser.uid,
          senderUid: currentUser.uid,
          senderName: userData.name || '',
          metadata: { targetUid },
        });
      }
    } catch (err) {
      console.error("Failed to send message:", err);
      // Removed alert() as it's not mobile-friendly
      console.error("Failed to send message.");
      setNewMessage(messageData.text); // Restore message on failure
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="chat-container">
      <div className="chat-header">
        <h3>Chat with {chatPartnerName}</h3>
      </div>
      
      <div className="chat-box">
        {loading ? (
          <p className="loading-state">Loading chat history...</p>
        ) : (
          messages.map(msg => (
            <div
              key={msg.id}
              className={`chat-message ${msg.senderUid === currentUser.uid ? "self" : "other"}`}
            >
              <div className="message-content">
                <div className="message-sender">
                  {msg.senderUid === currentUser.uid ? "You" : msg.senderName}
                </div>
                <p className="message-text">{msg.text}</p>
                <div className="message-meta">
                  {/* This will now display the full date and time */}
                  <span>{formatTimestamp(msg.timestamp)}</span>
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={scrollRef}></div>
      </div>
      
      <div className="chat-input-area">
        <input
          type="text"
          placeholder="Type a message..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && !isSending && sendMessage()}
          disabled={isSending}
        />
        <button onClick={sendMessage} disabled={!newMessage.trim() || isSending} className="action-btn-primary" data-tooltip="Send Message">
          {isSending ? <FaSpinner className="sending-indicator" /> : <FaPaperPlane />}
        </button>
      </div>
    </div>
  );
};

export default ChatRoom;